import { getFlattenedNodes, SummaryType } from "@ls/grouping-service";
import { compareValues, memoize, uniqueByKey } from "@ls/js-utils";
import { createSelector } from "reselect";
import CalcType from "state/Constants/CalcType";
import ScaleType from "state/Constants/ScaleType";
import SelectionState from "state/selections/SelectionState";
import {
  selectSelectedAccountEntityId,
  selectSelectedBenchmarkEntityId,
  selectSelectedAccountEntity,
} from "state/selections/selectors";
import {
  getEntitySummariesObject,
  getEntitySummariesObjectForBench,
} from "state/util";
import scope from "./scope";

export const selectAnalyticValues = (state) => state[scope]["analyticValues"];
export const selectFetchingAnalyticValues = (state) =>
  state[scope]["fetchingAnalyticsValues"];
export const selectAnalyticsData = (summaryType) => (state) =>
  state[scope]["analyticsData"][summaryType];
export const selectAnalyticsRatioData = (summaryType) => (state) =>
  state[scope]["analyticsRatioData"][summaryType];
export const selectFetchingAnalyticsData = (summaryType) => (state) =>
  state[scope]["fetchingAnalyticsData"][summaryType];
export const selectFetchingAnalyticsRatioData = (summaryType) => (state) =>
  state[scope]["fetchingAnalyticsRatioData"][summaryType];
export const selectAnalyticsSbsData = (summaryType) => (state) =>
  state[scope]["analyticsSbsData"][summaryType];
export const selectFetchingAnalyticsSbsData = (summaryType) => (state) =>
  state[scope]["fetchingAnalyticsSbsData"][summaryType];
export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return (state) => state[scope]["definitions"][selectionState];
  }

  return createSelector(
    (state) => state[scope]["definitions"][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(
        ({ complete }) => complete
      );
      return completeDefinitions.filter(
        uniqueByKey(completeDefinitions, "key")
      );
    }
  );
});

export const selectAnalyticValuesSelectionOptions = createSelector(
  selectAnalyticValues,
  (analyticValues) =>
    analyticValues
      ?.map((analyticValue) => ({
        label: analyticValue,
        value: analyticValue,
      }))
      ?.sort(compareValues("label")) ?? []
);

export const selectRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectAnalyticsData(SummaryType.ABSOLUTE),
  selectAnalyticsData(SummaryType.RELATIVE),
  selectAnalyticsRatioData(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (
    definitions,
    absData,
    relData,
    ratioData,
    accEntityId,
    bmkEntityId,
    benchId
  ) => {
    const absoluteNodes = getFlattenedNodes(absData?.root) ?? [];
    const relativeNodes = getFlattenedNodes(relData?.root) ?? [];
    const ratioNodes = getFlattenedNodes(ratioData?.root) ?? [];

    // Process absolute and relative nodes as before
    const rowMap = [...absoluteNodes, ...relativeNodes].reduce(
      (rowMap, node) => {
        const { pathString } = node;
        rowMap[pathString] = rowMap[pathString] ?? { pathString };
        const row = rowMap[pathString];
        definitions
          .filter(({ key }) => !row[key])
          .filter((definition) => definition.scaleType !== ScaleType.Ratio) // Exclude ratio definitions
          .forEach((definition) => {
            row[definition.key] = findAnalyticValue(
              node,
              definition,
              accEntityId,
              bmkEntityId,
              benchId?.benchmarkId
            );
          });
        return rowMap;
      },
      {}
    );

    // Process ratio nodes separately for ratio definitions only
    ratioNodes.forEach((node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      definitions
        .filter(({ key }) => !row[key])
        .filter((definition) => definition.scaleType === ScaleType.Ratio) // Only ratio definitions
        .forEach((definition) => {
          row[definition.key] = findAnalyticValue(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId
          );
        });
    });

    return rowMap;
  }
);

export const selectRowSbsMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectAnalyticsSbsData(SummaryType.ABSOLUTE),
  selectAnalyticsSbsData(SummaryType.RELATIVE),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, accEntityId, bmkEntityId, absSbsData, relSbsData, benchId) => {
    const absoluteSbsNodes = getFlattenedNodes(absSbsData?.root) ?? [];
    const relativeSbsNodes = getFlattenedNodes(relSbsData?.root) ?? [];
    let entityRowMap = {};
    const [absDefinitions, relDefinitions, bmkDefinitions] =
      splitByCalcType(definitions);
    absoluteSbsNodes.forEach((node) => {
      const { pathString } = node;
      let positionSummary;
      let filterNodeNotBmk;
      if (!!node.securityId) {
        positionSummary = node["positionSummary"]["entityResultMetaData"];
        filterNodeNotBmk = positionSummary.benchId === 0;
      } else {
        //entityResultMetaData = node["entitySummaries"]
        filterNodeNotBmk = false;
      }
      entityRowMap[pathString] = entityRowMap[pathString] ?? { pathString };
      const row = entityRowMap[pathString];

      absDefinitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          row[
            `${definition.key} - scenario${
              !!filterNodeNotBmk ? "benchmark" : ""
            }`
          ] = findAnalyticValue(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId
          );
        });
    }, {});
    relativeSbsNodes.forEach((node) => {
      const { pathString } = node;
      entityRowMap[pathString] = entityRowMap[pathString] ?? { pathString };
      const row = entityRowMap[pathString];
      relDefinitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          const value = findAnalyticValue(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId
          );
          if (value !== null && value !== undefined) {
            row[`${definition.key} - scenario`] = value;
          }
        });
    }, {});
    [...relativeSbsNodes, ...absoluteSbsNodes].forEach((node) => {
      const { pathString } = node;
      let positionSummary;
      let filterNodeNotBmk;
      if (!!node.securityId) {
        positionSummary = node["positionSummary"]["entityResultMetaData"];
        filterNodeNotBmk = positionSummary.benchId !== 0;
      } else {
        //entityResultMetaData = node["entitySummaries"]
        filterNodeNotBmk = false;
      }
      entityRowMap[pathString] = entityRowMap[pathString] ?? { pathString };
      const row = entityRowMap[pathString];
      bmkDefinitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          row[
            `${definition.key} - scenario${
              !!filterNodeNotBmk ? "notBenchmark" : ""
            }`
          ] = findAnalyticValue(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId
          );
        });
    }, {});
    return entityRowMap;
  }
);

const splitByCalcType = (definitions) => {
  const abs = [],
    bmk = [],
    rel = [];
  definitions?.forEach((definition) => {
    switch (definition.calcType) {
      case "Account":
        abs.push(definition);
        break;
      case "Relative":
        rel.push(definition);
        break;
      case "Benchmark":
        bmk.push(definition);
        break;
      default:
        throw new Error(
          `Unknown summary ${JSON.stringify(definition.calcType)}`
        );
    }
  });
  return [abs, rel, bmk];
};

const findAnalyticValue = (
  node,
  definition,
  accEntityId,
  bmkEntityId,
  benchId
) => {
  const { analytic, calcType, scaleType } = definition;
  const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
  const benchValue = calcType === CalcType.Benchmark ? 0 : benchId;
  const isRelative = calcType === CalcType.Relative;
  let entitySummariesObject;
  if (benchValue !== null && benchValue !== undefined) {
    entitySummariesObject = getEntitySummariesObjectForBench(
      node,
      entityId,
      benchValue,
      isRelative
    );
  } else {
    entitySummariesObject =
      getEntitySummariesObject(node, entityId, isRelative) ?? {};
  }
  let analyticValueMap;
  switch (scaleType) {
    case ScaleType.Contribution:
    case ScaleType.Ratio:
      analyticValueMap = entitySummariesObject?.["values"] ?? {};
      return analyticValueMap?.[analytic] ?? null;
    case ScaleType.WeightedAverage:
      analyticValueMap = entitySummariesObject?.["rescaledValues"] ?? {};
      return analyticValueMap?.[analytic] ?? null;
    case "25":
    case "75":
    case "50":
      // For percentiles, find the matching percentile value in the array
      const percentiles = entitySummariesObject?.["percentiles"] ?? [];
      const percentileValue = scaleType === "Median" ? 50 : parseInt(scaleType);
      const percentileData = percentiles.find(
        (p) => p.percentile === percentileValue
      );
      return percentileData?.[analytic] ?? null;
    default:
      throw new Error(`Unknown scaleType ${scaleType}`);
  }
};
